import { createScope, molecule } from "bunshi"
import { z } from "zod"

export const ENV_SCHEMA = z.object({
  NEXT_PUBLIC_BFF_BASE_URL: z.string(),
  NEXT_PUBLIC_DYNAMIC_ENVIRONMENT_ID: z.string(),
  NEXT_PUBLIC_LOGGER_URL: z.string(),
  DYNAMIC_API_BASE_URL: z.string().optional(),
  AMPLITUDE_API_KEY: z.string(),
  AMPLITUDE_SERVER_URL: z.string().optional(),
  THIRDWEB_CLIENT_ID: z.string(),
  IPFS_GATEWAY: z.string().optional(),
  BUILD_VERSION: z.string().optional(),
  NEXT_PUBLIC_PRIVY_APP_ID: z.string(),
  NEXT_PUBLIC_COLLECTOR_CRYPT_GACHA_BASE_URL: z.string(),
  NEXT_PUBLIC_GACHA_SOLANA_CLUSTER: z.string(),
  HOST_ENVIRONMENT: z.string().optional(),
  // Crossmint configuration
  NEXT_PUBLIC_CROSSMINT_CLIENT_API_KEY: z.string().optional(),
  // Coinflow configuration
  NEXT_PUBLIC_COINFLOW_MERCHANT_ID: z.string().optional(),
  NEXT_PUBLIC_COINFLOW_ENV: z.string().optional(),
  NEXT_PUBLIC_CHECKOUT_PROVIDER: z.string().optional(),
  // PostHog configuration
  // Unprefixed so they are read at request time; Turbopack inlines NEXT_PUBLIC_* at build time
  POSTHOG_KEY: z.string().optional(),
  POSTHOG_HOST: z.string().optional(),
  NEXT_PUBLIC_COINFLOW_SOLANA_ENABLED: z.string().optional(),
})

export type Env = z.infer<typeof ENV_SCHEMA>

export const ApplicationEnvironmentScope = createScope<Env>({
  NEXT_PUBLIC_BFF_BASE_URL: "",
  NEXT_PUBLIC_DYNAMIC_ENVIRONMENT_ID: "",
  NEXT_PUBLIC_LOGGER_URL: "",
  AMPLITUDE_API_KEY: "",
  AMPLITUDE_SERVER_URL: undefined,
  THIRDWEB_CLIENT_ID: "",
  IPFS_GATEWAY: undefined,
  BUILD_VERSION: undefined,
  NEXT_PUBLIC_PRIVY_APP_ID: "",
  NEXT_PUBLIC_COLLECTOR_CRYPT_GACHA_BASE_URL: "",
  NEXT_PUBLIC_GACHA_SOLANA_CLUSTER: "",
  HOST_ENVIRONMENT: undefined,
  // Crossmint configuration defaults
  NEXT_PUBLIC_CROSSMINT_CLIENT_API_KEY: undefined,
  // Coinflow configuration defaults
  NEXT_PUBLIC_COINFLOW_MERCHANT_ID: undefined,
  NEXT_PUBLIC_COINFLOW_ENV: undefined,
  NEXT_PUBLIC_CHECKOUT_PROVIDER: undefined,
  // PostHog configuration defaults
  POSTHOG_KEY: undefined,
  POSTHOG_HOST: undefined,
  NEXT_PUBLIC_COINFLOW_SOLANA_ENABLED: undefined,
})

export const ApplicationEnvironmentMolecule = molecule((_, scope) => {
  const {
    NEXT_PUBLIC_BFF_BASE_URL,
    NEXT_PUBLIC_DYNAMIC_ENVIRONMENT_ID,
    NEXT_PUBLIC_LOGGER_URL,
    DYNAMIC_API_BASE_URL,
    AMPLITUDE_API_KEY,
    AMPLITUDE_SERVER_URL,
    THIRDWEB_CLIENT_ID,
    IPFS_GATEWAY,
    BUILD_VERSION,
    NEXT_PUBLIC_PRIVY_APP_ID,
    NEXT_PUBLIC_COLLECTOR_CRYPT_GACHA_BASE_URL,
    NEXT_PUBLIC_GACHA_SOLANA_CLUSTER,
    HOST_ENVIRONMENT,
    NEXT_PUBLIC_CROSSMINT_CLIENT_API_KEY,
    NEXT_PUBLIC_COINFLOW_MERCHANT_ID,
    NEXT_PUBLIC_COINFLOW_ENV,
    NEXT_PUBLIC_CHECKOUT_PROVIDER,
    POSTHOG_KEY,
    POSTHOG_HOST,
    NEXT_PUBLIC_COINFLOW_SOLANA_ENABLED,
  } = scope(ApplicationEnvironmentScope)

  return {
    bffBaseUrl: NEXT_PUBLIC_BFF_BASE_URL,
    dynamicEnvironmentId: NEXT_PUBLIC_DYNAMIC_ENVIRONMENT_ID,
    dynamicApiBaseUrl: DYNAMIC_API_BASE_URL,
    amplitudeApiKey: AMPLITUDE_API_KEY,
    amplitudeServerUrl: AMPLITUDE_SERVER_URL,
    thirdwebClientId: THIRDWEB_CLIENT_ID,
    loggerUrl: NEXT_PUBLIC_LOGGER_URL,
    ipfsGateway: IPFS_GATEWAY,
    buildVersion: BUILD_VERSION,
    privyAppId: NEXT_PUBLIC_PRIVY_APP_ID,
    collectorCryptGachaBaseUrl: NEXT_PUBLIC_COLLECTOR_CRYPT_GACHA_BASE_URL,
    gachaSolanaCluster: NEXT_PUBLIC_GACHA_SOLANA_CLUSTER,
    hostEnvironment: HOST_ENVIRONMENT,
    // Crossmint configuration
    crossmintClientApiKey: NEXT_PUBLIC_CROSSMINT_CLIENT_API_KEY,
    // Coinflow configuration
    coinflowMerchantId: NEXT_PUBLIC_COINFLOW_MERCHANT_ID,
    coinflowEnv: NEXT_PUBLIC_COINFLOW_ENV,
    checkoutProvider: NEXT_PUBLIC_CHECKOUT_PROVIDER,
    // PostHog configuration
    posthogKey: POSTHOG_KEY,
    posthogHost: POSTHOG_HOST,
    /** Card checkout for Solana listings; off unless "true". */
    coinflowSolanaEnabled: NEXT_PUBLIC_COINFLOW_SOLANA_ENABLED === "true",
  }
})
